import { mayLeavePage } from "../navigation/leaveGuard";
import { Box, Button, Divider, Flex, Spinner, Text } from "@chakra-ui/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useListMyNotificationsQuery } from "../../autogen/bff-api";
import { ErrorBoundary } from "../errors/ErrorBoundary/ErrorBoundary";
import { useApiError } from "../errors/useApiError";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { urls } from "../../urls";
import { NotificationRow } from "./NotificationRow";
import { useMarkAllNotificationsRead } from "./useMarkAllNotificationsRead";

export const NOTIFICATION_DROPDOWN_LIMIT = 8;

interface Props {
  onClose: () => void;
}

export const NotificationDropdown = ({ onClose }: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const { data, isError, isLoading } = useListMyNotificationsQuery({
    limit: NOTIFICATION_DROPDOWN_LIMIT,
  });
  const { unreadCount, markAll, isMarking } = useMarkAllNotificationsRead();
  const notifications = data?.notifications ?? [];

  return (
    <Box w={{ base: "100vw", sm: "380px" }} maxW="380px">
      <Flex align="center" justify="space-between" px="4" py="3">
        <Text fontWeight="bold" fontSize="md" color="smText">
          {t("Alerts")}{" "}
          {unreadCount > 0 && (
            <Text as="span" color="smMuted" fontWeight="normal">
              · {t("{{count}} new", { count: unreadCount })}
            </Text>
          )}
        </Text>
        {unreadCount > 0 && (
          <Button
            variant="link"
            size="sm"
            colorScheme="teal"
            isLoading={isMarking}
            onClick={async () => {
              try {
                await markAll();
              } catch (e) {
                displayer.trigger(e);
              }
            }}
          >
            {t("Mark all as read")}
          </Button>
        )}
      </Flex>
      <Divider borderColor="smBorder" />
      <Box
        ref={listRef}
        tabIndex={-1}
        maxH="420px"
        overflowY="auto"
        _focus={{ outline: "none" }}
      >
        {/* Loading before empty: "you're all caught up" is an answer, and it must not be given
            before the alerts have been fetched — it flashed on every open. */}
        {isLoading ? (
          <Flex py="10" justify="center">
            <Spinner color="smPrimary" size="sm" />
          </Flex>
        ) : isError ? (
          <Flex py="10" justify="center">
            <Text color="smError" fontSize="sm">
              {t("Could not load alerts.")}
            </Text>
          </Flex>
        ) : notifications.length === 0 ? (
          <Flex py="10" justify="center">
            <Text color="smMuted" fontSize="sm">
              {t("You're all caught up")}
            </Text>
          </Flex>
        ) : (
          notifications.map((n, i) => (
            <Box
              key={n.id}
              borderTop={i === 0 ? "none" : "1px solid"}
              borderColor="smBorder"
            >
              <ErrorBoundary
                scope={`notification-row-${n.id}`}
                fallback={
                  <Flex px="4" py="3">
                    <Text color="smMuted" fontSize="sm">
                      {t("Couldn't show this alert.")}
                    </Text>
                  </Flex>
                }
              >
                <NotificationRow
                  notification={n}
                  selectedOrgId={authState.selectedOrg.id}
                  onNavigate={onClose}
                  onDismissStart={(id) =>
                    focusPastDismissed(listRef.current, id)
                  }
                />
              </ErrorBoundary>
            </Box>
          ))
        )}
      </Box>
      <Divider borderColor="smBorder" />
      <Flex justify="center" py="2.5">
        <Button
          variant="link"
          size="sm"
          colorScheme="teal"
          onClick={() => {
            if (mayLeavePage()) navigate(urls.app.notifications);
            onClose();
          }}
        >
          {t("See all alerts")} →
        </Button>
      </Flex>
    </Box>
  );
};

const focusPastDismissed = (list: HTMLElement | null, dismissedId: string) => {
  if (!list) return;
  const dismissButtons = [
    ...list.querySelectorAll<HTMLElement>("[data-dismiss-alert]"),
  ];
  const index = dismissButtons.findIndex(
    (button) => button.dataset.dismissAlert === dismissedId,
  );
  const neighbour = dismissButtons[index + 1] ?? dismissButtons[index - 1];
  (neighbour ?? list).focus();
};
